/**
 * LabelBlockEditor Component
 *
 * Editor for LabelBlock style fields (used in Text.parameters.labels)
 *
 * Sprint: screen-editor-body
 * Task: Phase 3 - LabelBlockEditor component
 */

import React from 'react';
import { Box } from '@mui/material';
import type { TemplateLabelBlock } from '@modules/reactFlow/types/templateLabelBlock.types';
import { StyleFieldRow } from '../styleFields/StyleFieldRow';
import {
  LABEL_BLOCK_STYLE_FIELDS,
  LABEL_BLOCK_PADDING_FIELDS,
} from '../../../config/atomicFields/labelBlockFields';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import { getStyleInputTestId } from '../../../utils/testIdUtils';

export interface LabelBlockEditorProps {
  /** Current LabelBlock value */
  value: Partial<TemplateLabelBlock>;
  /** Callback when any field changes */
  onChange: (value: Partial<TemplateLabelBlock>) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Use compact styling */
  compact?: boolean;
  /** Include padding fields (default: false) */
  includePadding?: boolean;
}

/**
 * LabelBlockEditor - renders style fields for editing a LabelBlock
 *
 * Uses LABEL_BLOCK_STYLE_FIELDS configuration for consistent field rendering.
 * Layout: 2-column grid matching AddLabelDialog design.
 */
export function LabelBlockEditor({
  value,
  onChange,
  disabled = false,
  compact = false,
  includePadding = false,
}: LabelBlockEditorProps): React.ReactElement {
  // Get the fields to render
  const fields: FieldConfig[] = includePadding
    ? [...LABEL_BLOCK_STYLE_FIELDS, ...LABEL_BLOCK_PADDING_FIELDS]
    : LABEL_BLOCK_STYLE_FIELDS;

  /**
   * Handle field change by extracting the field name from path
   * Path format: ".fieldName" -> "fieldName"
   */
  const handleFieldChange = (fieldPath: string, newValue: unknown) => {
    // Extract field name from path (e.g., ".fontFamily" -> "fontFamily")
    const fieldName = fieldPath.startsWith('.') ? fieldPath.slice(1) : fieldPath;

    onChange({
      ...value,
      [fieldName]: newValue,
    });
  };

  /**
   * Get value for a specific field
   */
  const getFieldValue = (fieldPath: string): unknown => {
    const fieldName = fieldPath.startsWith('.') ? fieldPath.slice(1) : fieldPath;
    return value[fieldName as keyof TemplateLabelBlock];
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: compact ? '1fr' : '1fr 1fr',
        gap: compact ? 1 : 2,
        width: '100%',
      }}
    >
      {fields.map((field) => (
        <StyleFieldRow
          key={field.path}
          config={field}
          value={getFieldValue(field.path)}
          onChange={(newValue) => handleFieldChange(field.path, newValue)}
          disabled={disabled}
          inputTestId={getStyleInputTestId('label-block', field.label)}
        />
      ))}
    </Box>
  );
}
